import {
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import {
  CrossIconLarge,
  CrossIconSmall,
  DocFileIcon,
  FileDeleteIcon,
  FileDownloadIcon,
  FilePermissionIcon,
  FileRenameIcon,
  FileViewIcon,
  FilesUploadIcon,
  FilterIconDark,
  FolderIcon,
  PdfFileIcon,
  PlusIcon,
  ResetIcon,
  SearchIconSM,
  XlsFileIcon,
  ZipFileIcon,
} from '../../assets/icons/icons'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { ExpandMoreOutlined } from '@mui/icons-material'
import {
  useAddFiles,
  useDeleteFiles,
  useGetAllUsers,
  useGetFiles,
  useUpdateFiles,
} from './hooks'
import amazonService from '../../lib/aws'
import { formatMimeType, formatSize } from './helpers'
import { useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import { SnackbarComponent } from '../../components/Snackbar'
import Papa, { ParseResult } from 'papaparse';
import * as XLSX from 'xlsx';
import SnackBarContext from '../../context/SnackBarContext'

export const Files = () => {
  const theme = useTheme()

  // Navigate
  const navigate = useNavigate()

  // Params
  const { id } = useParams()

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Use States
  const [search, setSearch] = useState('')

  const [isCreatingFolder, setIsCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('New Folder')
  const [isRenaming, setIsRenaming] = useState(null)
  const {toggleSnackbar}=useContext(SnackBarContext)
  const [filter, setFilter] = useState({})

  const [showMenu, setShowMenu] = useState(null)

  const [permissionModalOpen, setPermissionModalOpen] = useState(false)

  const [s3Loading, sets3Loading] = useState(false)

  const [selectedItem, setSelectedItem] = useState(null)

  const [fileNotSupported, setFileNotSupported] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // API Functions
  const { mutate: mutateAdd, isLoading: isLoadingAdd } =
    useAddFiles('add-files')

  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } =
    useUpdateFiles('update-files')

  const { data: allData, isFetching: isLoading } = useGetFiles(
    'get-files',
    filter
  )

  // Handle Search
  const handleSearch = () => {
    interface FilterOptions {
      search: string
      parentId?: string
    }

    const filterOptions: FilterOptions = {
      search: search,
    }

    if (id) {
      filterOptions.parentId = id
    }

    setFilter(filterOptions)
  }

  // Create Folder Start
  const handleCreateFolderStart = () => {
    setIsRenaming(null)
    setIsCreatingFolder(true)
    setNewFolderName('New Folder')
    setErrorMessage(null);
  }

  // Create Folder Complete
  const handleCreateFolderComplete = () => {
    if (!newFolderName.trim()) {
      setErrorMessage('Folder name cannot be empty');
      return;
    }

    const payload = {
      name: newFolderName,
      type: 'folder',
      items: null,
      parent: id,
    }

    mutateAdd(payload, {
      onSuccess: data => {
        setIsCreatingFolder(false)
      },
      onError: () => {},
    })
    setIsCreatingFolder(false)
  }

  // Handle Rename Start
  const handleRenameStart = (index: any, currentName: any) => {
    setIsCreatingFolder(true)
    setIsRenaming(index)
    const nameWithoutExtension =
      currentName?.split('.')?.slice(0, -1)?.join('.') || currentName
    setNewFolderName(nameWithoutExtension)
    setErrorMessage(null);
  }

  // Handle Rename Complete
  const handleRenameComplete = (file: any) => {
    if (!newFolderName.trim()) {
      setErrorMessage('Folder name cannot be empty');
      return;
    }

    const payload = {
      id: file.id,
      name: newFolderName,
      parent: id,
    }

    mutateUpdate(payload, {
      onSuccess: () => {
        setIsCreatingFolder(false)
        setIsRenaming(null)
        setNewFolderName('New Folder')
        setErrorMessage(null)
      },
      onError: () => {
        setIsCreatingFolder(false)
        setIsRenaming(null)
        setNewFolderName('New Folder')
        setErrorMessage(null);
      },
    })
  }

  // Get File Icons
  const getIcon = (mimeType: any, imagePath: any) => {
    if (!mimeType) return <FolderIcon />

    if (mimeType.includes('application/pdf')) {
      return <PdfFileIcon />
    } else if (
      mimeType.includes('application/zip') ||
      mimeType.includes('application/x-zip-compressed')
    ) {
      return <ZipFileIcon />
    } else if (mimeType && mimeType.includes('image')) {
      return (
        <img
          src={imagePath}
          alt='photo'
          style={{ width: '45px', height: '45px' }}
        />
      )
    } else if (
      mimeType.includes(
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) ||
      mimeType.includes('application/vnd.ms-excel') ||
      mimeType.includes('text/csv')
    ) {
      return <XlsFileIcon />
    } else if (
      mimeType.includes('application/msword') ||
      mimeType.includes(
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      )
    ) {
      return <DocFileIcon />
    } else {
      return null
    }
  }

  useEffect(() => {
    id && setFilter({ search: search, parentId: id })
  }, [id])
  const newlyAddedItemRef = useRef<HTMLElement | null>(null);
  const scrollToNewlyAddedItem = () => {
    if (newlyAddedItemRef.current) {
      newlyAddedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };
  // Handle File Upload to s3 bucket
  const handleFileUpload = async (event: any) => {
    const file = event.target.files[0]
    const duplicateFile = allData?.find((item: any) => item?.name === file?.name);
    if (duplicateFile) {
      toggleSnackbar(`${file?.name} is already uploaded`, "error");
      sets3Loading(false);
      return;
    }
    if (file) {
      const isVideo = file.type.startsWith('video/');
      if (isVideo) {
        setFileNotSupported(true); 
        return;
      } else {
        setFileNotSupported(false);
      } 
      const fileName = `${new Date().getTime()}_${file?.name}`

      try {
        sets3Loading(true)
        const uploadUrl = await amazonService().uploadFile(file, fileName)
        if (uploadUrl) {
          const payload = {
            name: file?.name,
            type: 'file',
            path: uploadUrl,
            mimeType: file?.type,
            size: file?.size,
            parent: id,
          }

          mutateAdd(payload, {
            onSuccess: data => {
              if (fileInputRef?.current) {
                fileInputRef.current.value = ''
              }
              sets3Loading(false)
              scrollToNewlyAddedItem()
              toggleSnackbar("File uploaded successfully","success")
            },
            onError: () => {
              if (fileInputRef?.current) {
                fileInputRef.current.value = ''
              }
              sets3Loading(false)
            },
          })
        } else {
          console.log('Failed to upload file.')
        }
      } catch (error) {
        console.error('Error uploading file:', error)
      }
    } else {
      console.log('No file selected.')
    }
  }

  return (
    <>
      <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
        <Box
          marginBottom={3}
          alignItems={'center'}
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'space-between'}>
          <Typography variant='h4'>Files</Typography>
          {fileNotSupported && (
            <Typography variant='body1' color='error'>
              File format not supported. Please upload a different file.
            </Typography>
          )}
          <Box>
            <Button
              variant='contained'
              sx={{
                padding: theme.spacing(1.5, 4, 1.5, 4),
                borderRadius: '8px',
                textTransform: 'capitalize',
                fontSize: '13px',
                backgroundColor: '#59A0A0',
                mr: 2,
                '&:hover': {
                  backgroundColor: '#59A0A0',
                },
              }}
              onClick={handleCreateFolderStart}>
              <PlusIcon /> <Box sx={{ width: 10 }} /> New Folder
            </Button>

            <Button
              variant='contained'
              sx={{
                padding: theme.spacing(1.5, 4, 1.5, 4),
                borderRadius: '8px',
                textTransform: 'capitalize',
                fontSize: '13px',
              }}
              onClick={() => {
                let input = document.getElementById('file-upload')
                input?.click()
              }}>
              <FilesUploadIcon /> <Box sx={{ width: 10 }} /> Upload Files
            </Button>

            <InputLabel htmlFor='file-upload'>
              <input
                id='file-upload'
                type='file'
                onChange={handleFileUpload}
                multiple
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
            </InputLabel>
          </Box>
        </Box>

        <Card
          sx={{
            padding: 1,
            boxShadow: 'none',
            border: '1px solid #EEEEEE',
            borderRadius: '8px',
            paddingInline: '20px',
            paddingTop: '15px',
            height: '68vh',
            width: '100%',
          }}>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              height: '10%',
            }}>
            <Box
              sx={{
                border: '2px solid #D7D4DE',
                padding: '0px 10px',
                borderRadius: '10px',
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
                mr: 2,
              }}>
              <SearchIconSM />
              <TextField
                id='standard-basic'
                label=''
                variant='standard'
                placeholder='Search files'
                value={search}
                onChange={(e: any) => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSearch()
                  }
                }}
                sx={{
                  paddingLeft: 1,
                  border: 'none !important',
                  width: '30vw',
                  outline: 'none',
                  '& input': {
                    fontSize: '16px',
                  },
                  '& div::after, & div::before, div:hover:not(.Mui-disabled, .Mui-error):before':
                    {
                      borderBottom: '0px solid black',
                    },
                }}
              />
            </Box>
            <Button
              variant='contained'
              sx={{
                padding: theme.spacing(1, 2, 1, 2),
                borderRadius: '10px',
                textTransform: 'capitalize',
                width: 120,
                fontSize: '13px',
                backgroundColor: '#EEEEEE',
                boxShadow: 'none',
                color: '#5F5C5F',
                zIndex: 1,
                '&:hover': {
                  boxShadow: 'none',
                  backgroundColor: '#EEEEEE',
                },
                position: 'relative',
              }}
              onClick={handleSearch}>
              <FilterIconDark /> <Box sx={{ width: 10 }} /> Filter
            </Button>

            <Button
              variant='contained'
              sx={{
                padding: theme.spacing(1, 2, 1, 2),
                borderRadius: '10px',
                textTransform: 'capitalize',
                fontSize: '13px',
                backgroundColor: '#EEEEEE',
                boxShadow: 'none',
                color: '#5F5C5F',
                zIndex: 1,
                '&:hover': {
                  boxShadow: 'none',
                  backgroundColor: '#EEEEEE',
                },
                position: 'relative',
                ml: 1,
              }}
              onClick={() => {
                setSearch('')
                setFilter({ ...filter, search: '' })
              }}>
              <ResetIcon colorBg /> <Box sx={{ width: 5 }} /> Reset Filter
            </Button>
          </Box>

          {allData?.length === 0 && !isCreatingFolder && (
            <Box
              sx={{
                width: '100%',
                height: '68vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Typography
                sx={{
                  color: 'gray',
                  fontSize: '20px',
                }}>
                No files found
              </Typography>
            </Box>
          )}

          <Grid
            container
            spacing={2}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              width: '100%',
              height:"80%",
              mt: 0,
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none',
              },            
            }}>
            {allData?.map((item: any, index: any) => (
              //@ts-ignore
              <Grid item key={index} ref={index === allData.length - 1 ? newlyAddedItemRef : null}>
                <Box
                  onClick={(e: any) => {
                    if (item.type === 'folder' && isRenaming === null) {
                      navigate(`/files/${item?.id}`, {
                        state: { folderDetails: item },
                      })
                    }
                  }}
                  sx={{
                    backgroundColor: '#F1EFF5',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 2.5,
                    position: 'relative',
                    height: '15vh',
                    ":hover":{
                      cursor:"pointer"
                    }
                  }}
                  onMouseEnter={() => setShowMenu(index)}
                  onMouseLeave={() => setShowMenu(null)}>
                  {getIcon(item?.mimeType, item?.path)}
                  {isRenaming === index ? (
                    <TextField
                      inputProps={{maxLength:20}}
                      autoFocus
                      variant='standard'
                      value={newFolderName}
                      onChange={e => setNewFolderName(e.target.value)}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          handleRenameComplete(item)
                        }
                      }}
                      sx={{
                        width: '80%',
                        margin: '10px auto',
                        textAlign: 'center',
                        display: 'block',
                        '& input': {
                          textAlign: 'center',
                        },
                      }}
                    />
                  ) : (
                    <>
                    <Tooltip title={item?.name || ''} placement="bottom">
                      <Typography
                        sx={{
                          color: '#2B262C',
                          fontSize: '15px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '200px', 
                          minHeight:'4vh',
                          padding:"0 20px"
                        }}>
                        {item?.name}
                      </Typography>
                      </Tooltip>
                      {item.type !== 'folder' ? (
                        <Tooltip title={`${item?.name} ${formatMimeType(item?.mimeType)} ${formatSize(item?.size)}`}>
                        <Typography sx={{ 
                          color: '#7C7A80', 
                          fontSize: '15px',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '200px', 
                          }}>
                          {item?.name.slice(0,8)}{' '}
                          {formatMimeType(item?.mimeType)}{' '}
                          {formatSize(item?.size)}
                        </Typography>
                        </Tooltip>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}>
                          <Typography
                            sx={{ color: '#7C7A80', fontSize: '15px' }}>
                            Folder
                          </Typography>
                          <Typography
                            sx={{ color: '#7C7A80', fontSize: '15px' }}>
                            {item?.folderCounts}{' '}
                            {item?.folderCounts === 0 ||
                            item?.folderCounts === 1
                              ? 'item'
                              : 'items'}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                  {showMenu === index && (
                    <FileMenu
                      permissionModalOpen={permissionModalOpen}
                      setPermissionModalOpen={setPermissionModalOpen}
                      file={item}
                      index={index}
                      handleRenameStart={handleRenameStart}
                      setSelectedItem={setSelectedItem}
                      toggleSnackbar={toggleSnackbar}
                      type={item?.type}
                    />
                  )}
                </Box>
                {errorMessage && isRenaming === index && (
                  <Typography variant='body1' color='error'>
                    {errorMessage}
                  </Typography>
                )}
              </Grid>
            ))}

            {isCreatingFolder && isRenaming === null && (
              <Grid item>
                <Box
                  sx={{
                    backgroundColor: '#F1EFF5',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 2.5,
                    position: 'relative',
                  }}>
                  <FolderIcon />

                  <TextField
                  inputProps={{maxLength:20}}
                    autoFocus
                    variant='standard'
                    value={newFolderName}
                    onChange={e => setNewFolderName(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        handleCreateFolderComplete()
                      }
                    }}
                    onBlur={handleCreateFolderComplete}
                    sx={{
                      width: '80%',
                      margin: '10px auto',
                      textAlign: 'center',
                      display: 'block',
                      '& input': {
                        textAlign: 'center',
                      },
                    }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Card>
      </Box>

      {selectedItem && (
        <ManagePermissionsModal
          openPermissionModal={permissionModalOpen}
          handleClosePermissionModal={() => setPermissionModalOpen(false)}
          file={selectedItem}
        />
      )}

      <Backdrop
        sx={{ color: '#F0845D', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={s3Loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </>
  )
}

const FileMenu = ({
  file,
  index,
  permissionModalOpen,
  setPermissionModalOpen,
  handleRenameStart,
  setSelectedItem,
  toggleSnackbar,
  type,
}: any) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  // API Functions
  const { mutate: mutateDelete, isLoading: isLoadingDelete } =
    useDeleteFiles('delete-files')

    const handleCancel = (e: any) => {
      e.stopPropagation()
      setDeleteModalOpen(false)
    }
  // Handle Close Menu
  const handleClose = (e: any) => {
    e.stopPropagation()
    setAnchorEl(null)
  }

  // Handle Delete
  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await mutateDelete(file?.id)
      setDeleteModalOpen(false)
      toggleSnackbar(`${type=="file" ? "File":"Folder"} deleted successfully`,"success")
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  }

  // Handle Download
  const handleDownload = async (url: any, filename: any) => {
    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error('Network response was not ok')
      const blob = await response.blob()
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.setAttribute('download', filename || 'download')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(link.href)
    } catch (error) {
      console.error('Failed to download the file:', error)
    }
  }

  interface CSVRow {
    [key: string]: string | undefined;
  }  
  const handleViewClick = async (e: any) => {
    e.stopPropagation();

    const filePath = file?.path || '';
      if (!filePath.endsWith('.csv') && !filePath.endsWith('.xlsx') && !filePath.endsWith('.xls')){
        e.stopPropagation()
        window.open(file?.path, '_blank')
        handleClose(e)
      }
      try {
        const response = await fetch(filePath);
        if (!response.ok) throw new Error('Network response was not ok');
        const fileBlob = await response.blob(); 
        const fileReader = new FileReader();
        fileReader.onload = (e) => {
          const arrayBuffer = e?.target?.result as ArrayBuffer; 
          if (arrayBuffer) {
            const data = new Uint8Array(arrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' }); 
            const sheetName = workbook.SheetNames[0]; 
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json<CSVRow>(worksheet, { header: 1 });
            const htmlContent = `
            <html>
              <head>
                <style>
                  table {
                    border-collapse: collapse;
                    width: 100%;
                  }
                  th, td {
                    border: 1px solid black;
                    padding: 8px;
                    text-align: left;
                  }
                </style>
              </head>
              <body>
                <h1>${file?.name}</h1>
                <table>
                ${jsonData.map((row: CSVRow) =>
                  `<tr>${Object.values(row).map((cell) =>
                  `<td>${cell !== undefined ? cell : 'N/A'}</td>`).join('')}
                  </tr>`).join('')}
                </table>
              </body>
            </html>
          `;
            const newTab = window.open();
            if (newTab) {
              newTab.document.write(htmlContent);
              newTab.document.close();
            } else {
              console.error('Failed to open a new tab.');
            }
          } else {
            console.error('Failed to read file content.');
          }
        };
        fileReader.readAsArrayBuffer(fileBlob);
      } catch (error) {
        console.error('Failed to view the file:', error);
      }
  };

  return (
    <>
      <Box
        sx={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          display: 'flex',
          paddingLeft: 3,
          cursor: 'pointer',
          position: 'absolute',
          top: 3,
          right: 3,
          zIndex: 99,
        }}
        onClick={(e: any) => e.stopPropagation()}>
        <IconButton
          onClick={(event: any) => {
            event.stopPropagation()
            setAnchorEl(event.currentTarget)
          }}>
          <svg
            width='5'
            height='17'
            viewBox='0 0 5 17'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M2.16683 0.167969C1.59219 0.167969 1.04109 0.396242 0.634765 0.802571C0.228436 1.2089 0.000162532 1.76 0.000162507 2.33464C0.000162482 2.90927 0.228436 3.46037 0.634765 3.8667C1.04109 4.27303 1.59219 4.5013 2.16683 4.5013C2.74146 4.5013 3.29256 4.27303 3.69889 3.8667C4.10522 3.46037 4.3335 2.90927 4.3335 2.33464C4.3335 1.76 4.10522 1.2089 3.69889 0.802571C3.29256 0.396242 2.74147 0.167969 2.16683 0.167969Z'
              fill='#A5A2A5'
            />
            <path
              d='M2.16683 6C1.59219 6 1.04109 6.22827 0.634765 6.6346C0.228436 7.04093 0.000162532 7.59203 0.000162507 8.16667C0.000162482 8.7413 0.228436 9.2924 0.634765 9.69873C1.04109 10.1051 1.59219 10.3333 2.16683 10.3333C2.74146 10.3333 3.29256 10.1051 3.69889 9.69873C4.10522 9.2924 4.3335 8.7413 4.3335 8.16667C4.3335 7.59203 4.10522 7.04093 3.69889 6.6346C3.29256 6.22827 2.74147 6 2.16683 6Z'
              fill='#A5A2A5'
            />
            <path
              d='M2.16683 11.8359C1.59219 11.8359 1.04109 12.0642 0.634765 12.4705C0.228436 12.8769 0.000162532 13.428 0.000162507 14.0026C0.000162482 14.5772 0.228436 15.1283 0.634765 15.5347C1.04109 15.941 1.59219 16.1693 2.16683 16.1693C2.74146 16.1693 3.29256 15.941 3.69889 15.5347C4.10522 15.1283 4.3335 14.5772 4.3335 14.0026C4.3335 13.428 4.10522 12.8769 3.69889 12.4705C3.29256 12.0642 2.74147 11.8359 2.16683 11.8359Z'
              fill='#A5A2A5'
            />
          </svg>
        </IconButton>
      </Box>

      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        PaperProps={{
          style: {
            borderRadius: 8,
            boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
          },
        }}>
        {file?.type === 'file' && (
          <MenuItem
            onClick={handleViewClick}
            sx={{
              gap: 2,
              py: 1,
              fontSize: '14px',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '5%',
                width: '90%',
                borderBottom: '1px solid #DEE2E0',
              },
            }}>
            <FileViewIcon /> View
          </MenuItem>
        )}

        {file?.type === 'file' && (
          <MenuItem
            onClick={e => {
              e.stopPropagation()
              handleDownload(file?.path, file?.name)
              handleClose(e)
            }}
            sx={{
              gap: 2,
              py: 1,
              fontSize: '14px',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '5%',
                width: '90%',
                borderBottom: '1px solid #DEE2E0',
              },
            }}>
            <FileDownloadIcon /> Download
          </MenuItem>
        )}

        <MenuItem
          sx={{
            gap: 2,
            py: 1,
            fontSize: '14px',
            '&:hover': {
              backgroundColor: '#f0f0f0',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '5%',
              width: '90%',
              borderBottom: '1px solid #DEE2E0',
            },
          }}
          onClickCapture={e => {
            e.stopPropagation()
            handleRenameStart(index, file?.name)
            handleClose(e)
          }}>
          <FileRenameIcon /> Rename
        </MenuItem>
        <MenuItem
          onClick={e => {
            handleClose(e)
            setSelectedItem(file)
            setPermissionModalOpen(true)
          }}
          sx={{
            gap: 2,
            py: 1,
            fontSize: '14px',
            '&:hover': {
              backgroundColor: '#f0f0f0',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '5%',
              width: '90%',
              borderBottom: '1px solid #DEE2E0',
            },
          }}>
          <FilePermissionIcon /> Manage Permission
        </MenuItem>
        <MenuItem
          onClick={handleClose}
          sx={{
            gap: 2,
            py: 1,
            fontSize: '14px',
            '&:hover': {
              backgroundColor: '#f0f0f0',
            },
          }}
          onClickCapture={(e: any) => {
            // handleDelete(e)
            setDeleteModalOpen(true);
            handleClose(e)
          }}>
          <FileDeleteIcon /> Delete
        </MenuItem>
      </Menu>
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onClick={(e)=>{
          e.stopPropagation()
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this {file?.name}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="primary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const ManagePermissionsModal = ({
  openPermissionModal,
  handleClosePermissionModal,
  file,
}: any) => {
  const theme = useTheme()

  const [selectedUsers, setSelectedUsers] = useState<any>([])
  const [selectedRole, setSelectedRole] = useState<any>('')

  const [savePermissionBtnLoading, setSavePermissionBtnLoading] =
    useState(false)

  const [snakbarOptions, setSnakbarOptions] = useState({
    open: false,
    type: '',
    message: '',
  })

  // API Functions
  const { data: allUsers, isFetching: isLoading } = useGetAllUsers('get users')

  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } =
    useUpdateFiles('update-files')

  // Get File Icons
  const getIcon = (mimeType: any, imagePath: any) => {
    if (!mimeType) return <FolderIcon />

    if (mimeType.includes('application/pdf')) {
      return <PdfFileIcon />
    } else if (
      mimeType.includes('application/zip') ||
      mimeType.includes('application/x-zip-compressed')
    ) {
      return <ZipFileIcon />
    } else if (mimeType && mimeType.includes('image')) {
      return (
        <img
          src={imagePath}
          alt='photo'
          style={{ width: '50px', height: '50px' }}
        />
      )
    } else if (
      mimeType.includes(
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) ||
      mimeType.includes('application/vnd.ms-excel') ||
      mimeType.includes('text/csv')
    ) {
      return <XlsFileIcon />
    } else if (
      mimeType.includes('application/msword') ||
      mimeType.includes(
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      )
    ) {
      return <DocFileIcon />
    } else {
      return null
    }
  }

  // Clear Data
  const clearData = () => {
    setSelectedUsers([])
    setSelectedRole('')
  }

  // Handle Save
  const handleSave = () => {
    const payload = {
      id: file?.id,
      users: selectedUsers,
      userRole: selectedRole?.toLowerCase(),
    }

    setSavePermissionBtnLoading(true)
    mutateUpdate(payload, {
      onSuccess: () => {
        setSavePermissionBtnLoading(false)
        handleClosePermissionModal()
        clearData()
        setSnakbarOptions({
          open: true,
          message: 'Permissions saved successfully!',
          type: 'success',
        })
      },
      onError: () => {
        setSavePermissionBtnLoading(false)
        handleClosePermissionModal()
        setSnakbarOptions({
          open: true,
          message: 'Something went wrong!',
          type: 'error',
        })
      },
    })
  }

  // Roles Array
  const userRoles = [
    {
      id: 1,
      name: 'superintendent',
    },
    {
      id: 2,
      name: 'worker',
    },
  ]

  // Use Effect
  useEffect(() => {
    setSelectedRole(file?.userRole || '')
    setSelectedUsers(file?.users)
  }, [file, openPermissionModal])

  return (
    <>
      <Dialog
        open={openPermissionModal}
        onClose={() => {
          handleClosePermissionModal()
          clearData()
        }}
        PaperProps={{
          style: {
            borderRadius: 12,
            paddingInline: '1.5rem',
            paddingBlock: '1.5rem',
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '400px',
            '@media (min-width:600px)': {
              width: '80%',
            },
            '@media (min-width:900px)': {
              width: '60%',
            },
          },
        }}>
        <DialogTitle
          sx={{
            padding: 0,
            mb: 2,
          }}>
          <Typography
            sx={{ color: '#2B262C', fontSize: '22px', fontWeight: 'bold' }}>
            Manage Permissions
          </Typography>
          <IconButton
            aria-label='close'
            onClick={() => {
              handleClosePermissionModal()
              clearData()
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 20,
              '&:hover': {
                backgroundColor: theme => theme.palette.grey[300],
              },
            }}
            >
            <CrossIconLarge />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            mb: 2,
            padding: 0,
          }}>
          <Typography
            gutterBottom
            variant='h6'
            component='div'
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span
              style={{
                width: '25px',
                height: '25px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}>
              {getIcon(file?.mimeType, file?.path)}
            </span>
            <Typography
              sx={{
                fontSize: '14px',
                color: '#2B262C',
                ml: file?.mimeType?.includes('image') ? 2 : 0,
              }}>
              {file?.name}
            </Typography>
          </Typography>

          <FormControl
            sx={{
              width: '100%',
              fontSize: '13px',
              color: '#ACB9B5',
              mt: 3,
            }}>
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #DBDBDB',
                '&:hover': {
                  borderColor: '#F0845D',
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
                },
              }}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}>
              <Select
                id='demo-simple-select'
                value={selectedUsers}
                onChange={e => {
                  setSelectedUsers(e.target.value)
                }}
                displayEmpty
                multiple
                IconComponent={ExpandMoreOutlined}
                sx={{
                  width: '100%',
                  border: 'none',
                  boxShadow: 'none',
                  padding: theme.spacing(0.8, 1.6, 0.8, 1.6),
                  '& .MuiSelect-select': {
                    padding: theme.spacing(0.8, 0, 0.8, 0),
                  },
                  '&:hover, & fieldset, & fieldset:hover': {
                    border: 'none',
                    margin: 0,
                    boxShadow: 'none',
                  },
                }}
                renderValue={selected => {
                  if (selected.length === 0) {
                    return (
                      <Typography
                        sx={{
                          color: 'gray',
                        }}>
                        Select User to Share With
                      </Typography>
                    )
                  }

                  return selected
                    ?.map((id: any) => {
                      const user = allUsers?.find((_: any) => _?.id == id)
                      return user?.firstName + ' ' + user?.lastName
                    })
                    .join(', ')
                }}>
                <MenuItem disabled value=''>
                  <Typography
                    sx={{
                      color: 'gray',
                    }}>
                    Select User to Share With
                  </Typography>
                </MenuItem>

                {allUsers?.map((item: any, index: any) => (
                  <MenuItem key={index} value={item?.id}>
                    {item?.firstName + ' ' + item?.lastName}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </FormControl>

          <FormControl
            sx={{
              width: '100%',
              fontSize: '13px',
              color: '#ACB9B5',
              mt: 3,
            }}>
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #DBDBDB',
                '&:hover': {
                  borderColor: '#F0845D',
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
                },
              }}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}>
              <Select
                value={selectedRole}
                onChange={e => {
                  setSelectedRole(e.target.value)
                }}
                IconComponent={ExpandMoreOutlined}
                sx={{
                  width: '100%',
                  border: 'none',
                  boxShadow: 'none',
                  padding: theme.spacing(0.8, 1.6, 0.8, 1.6),
                  '& .MuiSelect-select': {
                    padding: theme.spacing(0.8, 0, 0.8, 0),
                  },
                  '&:hover, & fieldset, & fieldset:hover': {
                    border: 'none',
                    margin: 0,
                    boxShadow: 'none',
                  },
                }}
                displayEmpty>
                <MenuItem value='' disabled>
                  <Typography
                    sx={{
                      color: 'gray',
                    }}>
                    Share by Role
                  </Typography>
                </MenuItem>

                {userRoles?.map((item: any, index: any) => (
                  <MenuItem key={item?.name} value={item?.name}>
                    {item?.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </FormControl>

          {selectedUsers?.length > 0 && (
            <Typography
              sx={{
                color: '#5F5C5F',
                mt: 3,
              }}>
              Users with access
            </Typography>
          )}

          {selectedUsers?.map((id: any, index: any) => {
            var item = allUsers?.find((_: any) => _.id == id)
            return (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 2,
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <Avatar
                      src={item?.idPhoto}
                      alt='image'
                      sx={{ marginRight: 1, width: 25, height: 25 }}
                    />
                    <Typography
                      sx={{
                        color: '#5F5C5F',
                        fontSize: '14px',
                      }}>
                      {item?.firstName + ' ' + item?.lastName}
                    </Typography>
                  </Box>

                  <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        "&:hover": {
                          backgroundColor: theme => theme.palette.grey[200],
                        },
                        borderRadius: '50%',
                        width: 35,
                        height: 35,
                      }
                    }
                    onClick={() => {
                      setSelectedUsers((prevUsers: any) =>
                        prevUsers?.filter((user: any) => user !== id)
                      )
                    }}>
                    <CrossIconSmall />
                  </Box>
                </Box>
              </>
            )
          })}

          {selectedRole && (
            <>
              <Typography
                sx={{
                  color: '#5F5C5F',
                  mt: 3,
                }}>
                Roles with access
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 2,
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                  <Typography
                    sx={{
                      color: '#5F5C5F',
                      fontSize: '14px',
                    }}>
                    {selectedRole}
                  </Typography>
                </Box>

                <Box
                   sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    "&:hover": {
                      backgroundColor: theme => theme.palette.grey[200],
                    },
                    borderRadius: '50%',
                    width: 35,
                    height: 35,
                  }
                }
                  onClick={() => {
                    setSelectedRole('')
                  }}>
                  <CrossIconSmall />
                </Box>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: 0,
            mt: 3,
          }}>
          <Button
            variant='contained'
            sx={{
              color: '#24212C',
              fontSize: '12px',
              background: '#DCDAE1',
              textTransform: 'none',
              border: '1px solid #7C7A80',
              borderRadius: '10px',
              py: 1.5,
              width: '25%',
              boxShadow: 'none',
              ':hover': {
                background: '#DCDAE1',
                boxShadow: '1px',
                border: '1px solid #F0845D',
              },
            }}
            onClick={() => {
              handleClosePermissionModal()
              clearData()
            }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            sx={{
              color: '#FFF',
              background: '#F0845D',
              fontSize: '12px',
              textTransform: 'none',
              borderRadius: '10px',
              py: 1.5,
              width: '75%',
              boxShadow: 'none',
              ':hover': {
                background: '#F0845D',
                boxShadow: '1px',
              },
            }}
            onClick={() => {
              handleSave()
            }}>
            {savePermissionBtnLoading ? (
              <CircularProgress
                sx={{ color: 'white', width: 20, height: 20 }}
                size={22}
              />
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarComponent
        severity={snakbarOptions.type}
        message={snakbarOptions.message}
        open={snakbarOptions.open}
        onClose={() => {
          setSnakbarOptions(prev => ({
            ...prev,
            open: false,
          }))
        }}
      />
    </>
  )
}
